import React, { useState, useEffect } from 'react';
import { Registration, relationshipTypesApi, RelationshipType } from '../../api/client';

interface EditRegistrationModalProps {
  registration: Registration;
  onSave: (updates: Partial<Registration>) => void;
  onClose: () => void;
}

const EditRegistrationModal: React.FC<EditRegistrationModalProps> = ({
  registration,
  onSave,
  onClose,
}) => {
  const [editForm, setEditForm] = useState<Partial<Registration>>({});
  const [relationshipTypes, setRelationshipTypes] = useState<RelationshipType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    // Invitation-only records approved without the invitee completing the form
    // carry just the combined name, so fall back to splitting that.
    let givenName = registration.given_name || '';
    let familyName = registration.family_name || '';
    if (!givenName && !familyName && registration.name) {
      const parts = registration.name.trim().split(/\s+/);
      givenName = parts[0] || '';
      familyName = parts.slice(1).join(' ');
    }
    setEditForm({
      given_name: givenName,
      family_name: familyName,
      pronouns: registration.pronouns || '',
      email: registration.email || '',
      company: registration.company || '',
      role: registration.role || '',
      relationship_type: registration.relationship_type || '',
      dietary_requirements: registration.dietary_requirements || '',
      non_dietary_requirements: registration.non_dietary_requirements || '',
      how_heard: registration.how_heard || '',
      notes: registration.notes || '',
      newsletter_opt_in: registration.newsletter_opt_in || false,
    });
    loadRelationshipTypes();
  }, [registration]);

  const loadRelationshipTypes = async () => {
    try {
      const types = await relationshipTypesApi.getRelationshipTypes();
      // Only show active types
      setRelationshipTypes(types.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load relationship types:', error);
      // Fallback to default types if API fails
      setRelationshipTypes([
        { id: 1, organization_id: 1, name: 'existing_client', display_name: 'Existing client', is_active: true, created_at: '', updated_at: '' },
        { id: 2, organization_id: 1, name: 'prospective_client', display_name: 'Prospective client', is_active: true, created_at: '', updated_at: '' },
        { id: 3, organization_id: 1, name: 'partner', display_name: 'Partner', is_active: true, created_at: '', updated_at: '' },
        { id: 4, organization_id: 1, name: 'staff', display_name: 'Staff', is_active: true, created_at: '', updated_at: '' }
      ]);
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(editForm);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-4xl p-6 h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Edit Registration</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 border-b pb-2">Basic Information</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Given Name</label>
                  <input
                    type="text"
                    value={editForm.given_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, given_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Family Name</label>
                  <input
                    type="text"
                    value={editForm.family_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, family_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pronouns</label>
                <input
                  type="text"
                  value={editForm.pronouns || ''}
                  onChange={(e) => setEditForm({ ...editForm, pronouns: e.target.value })}
                  placeholder="e.g., they/them"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Professional Information */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 border-b pb-2">Professional Information</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  value={editForm.company || ''}
                  onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role/Job Title</label>
                <input
                  type="text"
                  value={editForm.role || ''}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship Type</label>
                <select
                  value={editForm.relationship_type || ''}
                  onChange={(e) => setEditForm({ ...editForm, relationship_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  disabled={loadingTypes}
                >
                  <option value="">Select type...</option>
                  {relationshipTypes.map((type) => (
                    <option key={type.id} value={type.name}>
                      {type.display_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">How did they hear about us?</label>
                <input
                  type="text"
                  value={editForm.how_heard || ''}
                  onChange={(e) => setEditForm({ ...editForm, how_heard: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Requirements */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 border-b pb-2">Requirements</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dietary Requirements</label>
                <textarea
                  value={editForm.dietary_requirements || ''}
                  onChange={(e) => setEditForm({ ...editForm, dietary_requirements: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Other Requirements</label>
                <textarea
                  value={editForm.non_dietary_requirements || ''}
                  onChange={(e) => setEditForm({ ...editForm, non_dietary_requirements: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Admin Notes */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 border-b pb-2">Admin Notes</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                <textarea
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={4}
                  placeholder="Notes visible only to organizers..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="newsletter"
                  checked={editForm.newsletter_opt_in || false}
                  onChange={(e) => setEditForm({ ...editForm, newsletter_opt_in: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="newsletter" className="ml-2 block text-sm text-gray-700">
                  Newsletter opt-in
                </label>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRegistrationModal;